// Web-Push-Hilfsfunktionen (Browser-seitig) — siehe docs/notifications.md für
// den vollen Ablauf (VAPID, Service Worker, API-Endpunkte).

/** Browser/Betriebssystem unterstützt Web Push grundsätzlich (Service
 * Worker + Push API + Notification API). iOS/Safari zählt hier schon mit —
 * die eigentliche "nur als installierte App"-Einschränkung prüft
 * `isIosSafariNonStandalone()` separat, siehe dort. */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** iOS/iPadOS-Safari unterstützt Web Push erst ab Version 16.4, und nur,
 * wenn die Seite "Zum Home-Bildschirm hinzufügen" als eigene App installiert
 * wurde (reines Browser-Tab reicht nicht) — Apples Einschränkung, nicht
 * unsere. `navigator.standalone` ist Safaris eigenes (nicht standardisiertes)
 * Flag dafür; `display-mode: standalone` ist der Standard-Weg und deckt auch
 * andere Browser/Plattformen ab, falls dort einmal ähnliche Regeln gelten. */
export function isIosSafariNonStandalone(): boolean {
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  if (!isIos) return false;
  const standalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
  return !standalone;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const base64Safe = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64Safe);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

export async function getExistingPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

/** Fragt (falls nötig) die Benachrichtigungs-Berechtigung ab und legt ein
 * neues Push-Abo an. Wirft, wenn der Nutzer die Berechtigung ablehnt — vom
 * Aufrufer abzufangen und als Fehlermeldung anzuzeigen. */
export async function subscribeToPush(vapidPublicKey: string): Promise<PushSubscription> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('permission_denied');
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    // `Uint8Array<ArrayBufferLike>` vs. das striktere `BufferSource`
    // (`ArrayBuffer`-gebunden) in neueren TS-DOM-Typen — zur Laufzeit ein
    // ganz normales `Uint8Array`, das jeder Browser hier akzeptiert.
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
  });
}

export async function unsubscribeFromPush(subscription: PushSubscription): Promise<void> {
  await subscription.unsubscribe();
}
